// details-disclosure.js
// ✅ Keeps <a> click navigation intact
// ✅ Opens/closes <details> on hover (desktop only) so "toggle" fires and HeaderMenu.onToggle runs
// ✅ Includes small close delay to avoid flicker
// ✅ Defensive guards for missing elements

class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");
    if (!this.mainDetailsToggle) return;

    this.summary = this.mainDetailsToggle.querySelector("summary");
    this.content = this.summary ? this.summary.nextElementSibling : null;

    this.mainDetailsToggle.addEventListener("focusout", this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener("toggle", this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.content) return;

    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute("open")) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    if (!this.mainDetailsToggle) return;
    this.mainDetailsToggle.removeAttribute("open");
    const s = this.mainDetailsToggle.querySelector("summary");
    if (s) s.setAttribute("aria-expanded", false);
  }
}

customElements.define("details-disclosure", DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();

    this.header = document.querySelector(".header-wrapper");

    // Hover behavior (desktop only)
    this._closeTimer = null;
    this._desktopHoverMQ = window.matchMedia("(hover: hover) and (pointer: fine)");

    this._onPointerEnter = this._onPointerEnter.bind(this);
    this._onPointerLeave = this._onPointerLeave.bind(this);
  }

  connectedCallback() {
    // Only enable hover-open on real desktop pointers (mouse/trackpad)
    if (!this._desktopHoverMQ.matches) return;

    // Use pointer events so we can ignore touch
    this.addEventListener("pointerenter", this._onPointerEnter);
    this.addEventListener("pointerleave", this._onPointerLeave);
  }

  disconnectedCallback() {
    this.removeEventListener("pointerenter", this._onPointerEnter);
    this.removeEventListener("pointerleave", this._onPointerLeave);
    if (this._closeTimer) clearTimeout(this._closeTimer);
    this._closeTimer = null;
  }

  _onPointerEnter(e) {
    // Ignore non-mouse pointers
    if (e.pointerType && e.pointerType !== "mouse") return;

    if (this._closeTimer) clearTimeout(this._closeTimer);
    this._closeTimer = null;

    if (!this.mainDetailsToggle) return;

    // Open on hover; this triggers the native "toggle" event
    if (!this.mainDetailsToggle.open) {
      this.mainDetailsToggle.open = true;
    }
  }

  _onPointerLeave(e) {
    if (e.pointerType && e.pointerType !== "mouse") return;

    if (this._closeTimer) clearTimeout(this._closeTimer);

    // Small delay prevents flicker when moving into the dropdown content
    this._closeTimer = setTimeout(() => {
      this.close(); // Removes "open" and triggers "toggle" if state changed
    }, 120);
  }

  onToggle() {
    // Your original HeaderMenu behavior
    // (Note: this runs when <details> open state changes)

    if (!this.header || !this.mainDetailsToggle) return;

    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue("--header-bottom-position-desktop") !== "") return;

    document.documentElement.style.setProperty(
      "--header-bottom-position-desktop",
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define("header-menu", HeaderMenu);
